import { selectCurrUser } from '@/store/bootstrap/selectors';
import { Flex, Box } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CollectScoreCardForm } from './CollectScoreCardForm';
import { getInitialValues, getScoreCardFormConfig } from './helper';
import { ScoreCardCollapse } from './ScoreCardCollapse';
import { ScorecardHeader } from './ScorecardHeader';
import { useScoreCardAI } from '@/hooks/use-scorecardAI';
import { EmptyResults } from '@/components/ats/empty-results';
import NoTask from '@/assets/icons/ats/NoTask';
import { OverallEvaluationBanner } from './OverallEvaluationBanner';
import { useShowToast } from '@/hooks/useShowToast';
import './interviewScorecard.css';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { Edit05 } from '@kula-ai/icons/outline/Edit05';
import { KText } from '@/ui-library/components/text';
import { AiButton } from '@kula-ai/components';
import { AIFillScorecardLoader } from '@/components/shared/scorecard';
import { Button } from '@kula-ai/components';
import type { ScorecardBodyProps } from './ScorecardBody.interface';

const SectionHeader = ({ name, count, styles, editButton, aiButton }) => {
  return (
    <Flex alignItems="center" gap="12px" {...styles}>
      <Flex
        alignItems="center"
        gap="6px"
        bg="secondary-50"
        px="6px"
        py="4px"
        borderRadius="6px"
      >
        <KText as="span" size="sm-medium" color="secondary-900">
          {name}
        </KText>
        <Flex
          alignItems="center"
          bg="base-white"
          px="6px"
          borderRadius="6px"
          border="1px solid"
          borderColor="border-color"
        >
          <KText as="span" size="sm-semi-bold" color="secondary-700">
            {count}
          </KText>
        </Flex>
      </Flex>
      <Box borderBottom="1px solid" borderColor={'border-color'} flex={1}></Box>{' '}
      {editButton()}
      {aiButton()}
    </Flex>
  );
};

export const ScorecardBody = ({
  scoreCardDetails = [],
  formRef,
  handleSubmit = () => {},
  onSuccess = () => {},
  showOnlyCurrUser = false,
  canFillForOthers = false,
  canSubmit = false,
  isEdit = true,
  skipHeader = false,
  expandOnly = null,
  viewSc = false,
  setIsEdit = () => {},
  appData = {},
  transcript,
  isViewOnly = false,
  handleAutoSave = () => {},
  isV2: _isV2 = false,
  isMobileScrollable = false,
  applicationId = null,
  jobId = null,
  showCandidateLink = false,
}: ScorecardBodyProps) => {
  const isMobile = useIsMobile();
  const currUser = useSelector(selectCurrUser);
  const currUserScorecard = scoreCardDetails?.find(
    (item) => item?.reviewer?.id === currUser?.id
  );
  // Fall back to data[0] when admin (canFillForOthers) is not a reviewer themselves.
  const scoreCard = showOnlyCurrUser
    ? (currUserScorecard ?? scoreCardDetails?.[0])
    : scoreCardDetails?.[0];

  const {
    id,
    rating,
    feedback,
    questions,
    feedback_description,
    rating_description,
    status,
    submitted_by,
  } = scoreCard || {};

  const [edit, setEdit] = useState(!viewSc);
  const isSomeOtherUserHasDraftScorecard =
    status === 'draft' && submitted_by?.id && submitted_by?.id !== currUser?.id;

  const [initialValues, setInitialValues] = useState(
    getInitialValues({
      isSubmitted: isSomeOtherUserHasDraftScorecard ? false : true,
      values: {
        rating,
        feedback,
        questions,
        feedback_description,
        rating_description,
        attachments: scoreCard?.attachments || [],
        feedback_attachments: scoreCard?.feedback_attachments || [],
      },
    })
  );

  const updateInitialValues = ({
    type,
    attachments,
    attachmentKey,
    attachmentId,
  }) => {
    setInitialValues((prev) => {
      const newValues = { ...formRef?.current?.values };

      if (type === 'add' && attachments && attachmentKey) {
        newValues[attachmentKey] = [
          ...(prev[attachmentKey] || []),
          ...attachments,
        ];
      } else if (type === 'remove' && attachmentKey && attachmentId) {
        newValues[attachmentKey] = (prev[attachmentKey] || []).filter(
          (attachment) => attachment.id !== attachmentId
        );
      } else if (type === 'updateError' && attachments && attachmentKey) {
        // Mark the failed files with hasError flag
        const currentAttachments = prev[attachmentKey] || [];
        newValues[attachmentKey] = currentAttachments.map((file) => {
          // Check if this file is in the failed files list (match by name and size)
          const isFailed = attachments.some(
            (failedFile) =>
              failedFile.name === file.name &&
              failedFile.size === file.size &&
              file.hasError
          );
          return isFailed ? { ...file, hasError: true } : file;
        });
      }

      return newValues;
    });
  };

  const [isAiPolling, setAiPolling] = useState(false);
  const [showAiBanner, setShowAiBanner] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const showToast = useShowToast();

  const formConfig = useMemo(
    () =>
      id
        ? getScoreCardFormConfig({
            questions,
            canSubmit,
            isEdit: isEdit && edit,
            initialValues,
            scoreCardDetails: scoreCard,
            canFillForOthers,
            updateInitialValues,
            showToast,
            isMobile,
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, isEdit, edit, initialValues, canFillForOthers, isMobile]
  );

  const handleAiSuccess = (scorecard, error = false) => {
    if (!error && scorecard) {
      const newValues = getInitialValues({
        isSubmitted: true,
        values: scorecard,
      });
      setInitialValues(newValues);
      setShowAiBanner(true);
      // Remount so the rich-text editors re-seed from the AI-filled content.
      setFormKey((prev) => prev + 1);
    }
    setAiPolling(false);
  };

  const { handleFillWithAi, isFilling, isPolling } = useScoreCardAI({
    ats_application_id: scoreCard?.ats_application_id,
    scorecardId: scoreCard?.id,
    interviewId: appData?.id,
    onSuccess: handleAiSuccess,
  });

  const showAiLoading = isFilling || isAiPolling || isPolling;
  const isFillWithAi = transcript?.transcript?.length > 0;

  const isSingleScorecard = scoreCardDetails?.length === 1 || showOnlyCurrUser;

  const showEditIcon = viewSc && !edit && !isViewOnly;

  const editButton = () => {
    if (!showEditIcon) return null;

    return (
      <Button
        variant="tertiary"
        onClick={() => {
          setIsEdit?.(true);
          setEdit(true);
        }}
        data-test-id="single-scorecard-banner-edit-icon"
        leftElement={<Edit05 width={16} height={16} />}
      >
        Edit
      </Button>
    );
  };

  const aiButton = () => {
    if (!isFillWithAi || (viewSc && !edit)) return null;

    const handleAiFill = async (e) => {
      e?.stopPropagation();
      const success = await handleFillWithAi();
      if (success) {
        setAiPolling(true);
      }
    };

    return (
      <AiButton onClick={handleAiFill} withShadow={false}>
        Fill with AI
      </AiButton>
    );
  };

  const header = () => {
    if (skipHeader) return null;

    return (
      <ScorecardHeader
        scoreCardDetails={scoreCardDetails}
        showOnlyCurrUser={showOnlyCurrUser}
        editButton={isSingleScorecard ? null : editButton}
        aiButton={isSingleScorecard ? null : aiButton}
        isMobile={isMobile}
        applicationId={applicationId}
        jobId={jobId}
        showCandidateLink={showCandidateLink}
      />
    );
  };

  const body = () => {
    if (isSingleScorecard) {
      if (showAiLoading) {
        return <AIFillScorecardLoader />;
      }
      return (
        <Flex w="100%" flexDir="column">
          <SectionHeader
            styles={{ p: isMobile ? '12px 0px' : '12px 21px' }}
            name="Questions"
            count={formConfig?.length}
            editButton={editButton}
            aiButton={aiButton}
          />
          <Flex w="100%" p="0px 20px" pb="12px">
            <CollectScoreCardForm
              formRef={formRef}
              scorecard={scoreCard}
              handleSubmit={handleSubmit}
              formConfig={formConfig}
              initialValues={initialValues}
              handleAutoSave={handleAutoSave}
              status={status}
              disabled={!(canSubmit && isEdit && edit)}
              ratingBanner={showAiBanner ? <OverallEvaluationBanner /> : null}
              formKey={formKey}
            />
          </Flex>
        </Flex>
      );
    }

    // If expandOnly is set but no scorecard matches, show "Not Required Anymore"
    const hasMatchingScorecard =
      !expandOnly || scoreCardDetails?.some((sc) => +expandOnly === sc?.id);

    if (!hasMatchingScorecard) {
      return (
        <EmptyResults
          icon={NoTask}
          wrapperProps={{ h: '100%', flex: 1 }}
          subText="SCORECARD UNAVAILABLE"
          subTextProps={{ color: 'secondary-600' }}
          text="This scorecard is no longer accepting responses"
          textProps={{ color: 'secondary-900' }}
          iconProps={{ h: '140px', w: '130px', mb: '0px' }}
        />
      );
    }

    return (
      <Flex
        flexDir="column"
        gap={isMobile ? '8px' : '12px'}
        data-test-id="scorecard-body-container"
        p={isMobile ? '0px' : '0 16px'}
      >
        {scoreCardDetails?.map((scoreCard) => {
          return (
            <ScoreCardCollapse
              key={scoreCard?.id}
              scoreCard={scoreCard}
              onSuccess={onSuccess}
              canFillForOthers={canFillForOthers}
              expandOnly={expandOnly}
              transcript={transcript}
              interviewData={appData}
            />
          );
        })}
      </Flex>
    );
  };

  return (
    <Flex
      flexDir="column"
      w="100%"
      h={isMobileScrollable ? 'auto' : '100%'}
      gap={isMobile ? '12px' : '0'}
    >
      {header()}
      {body()}
    </Flex>
  );
};
